import { motion } from "framer-motion";
import { Wine, MapPin, TrendingUp, Eye, Phone, Star, ArrowRight, Users, Camera, Globe } from "lucide-react";

const STATS = [
  { value: "+87%", label: "Visualizzazioni Google Maps", icon: Eye },
  { value: "2x", label: "Visite in cantina dopo il tour", icon: Users },
  { value: "+45%", label: "Traffico al sito web", icon: Globe },
  { value: "+35%", label: "Clic su indicazioni stradali", icon: TrendingUp },
];

const BIG_PLAYERS = [
  { name: "Antinori nel Chianti Classico", zone: "Chianti", note: "Migliore cantina al mondo 2022 (World's Best Vineyards)", type: "Cantina Premium" },
  { name: "Sanlorenzo Winery", zone: "Montalcino", note: "Top per tradizione e autenticita, zona Brunello", type: "Cantina" },
  { name: "Tenuta Pakravan Papi", zone: "Montalcino", note: "Eccellenza Brunello di Montalcino", type: "Cantina" },
  { name: "Fattoria La Maliosa", zone: "Maremma", note: "Premium, zona Montecucco", type: "Cantina Bio" },
  { name: "Celestino Pecci", zone: "Montalcino", note: "Visite e degustazioni tutto l'anno", type: "Cantina" },
  { name: "Vineria di Montepulciano", zone: "Montepulciano", note: "5 vini top, 1h degustazione, centro storico", type: "Enoteca" },
  { name: "Agriturismo Cesani", zone: "San Gimignano", note: "363 recensioni, vigneti e panorama", type: "Cantina + Agriturismo" },
];

const WA_MSG = "Ciao Riccardo! Ho una cantina/enoteca in Toscana e vorrei il Virtual Tour 360 per farla vedere su Google Maps.";

export default function LandingCantine() {
  return (
    <div className="min-h-screen bg-[#0c1220] text-white">
      <div className="max-w-4xl mx-auto px-4 py-10">

        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FBBC04]/10 border border-[#FBBC04]/30 text-[#FBBC04] text-sm font-bold mb-6">
            <Wine className="w-4 h-4" /> CANTINE, ENOTECHE, BOTTEGHE ARTIGIANE
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-6">
            La tua cantina e' unica. <br />
            <span className="text-[#FBBC04]">Su Google sembra uguale a tutte.</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            I turisti scelgono dove andare dal cellulare. Se vedono 4 foto statiche della tua cantina,
            <strong className="text-white"> vanno da chi gli fa vivere l'esperienza prima ancora di arrivare.</strong>
          </p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {STATS.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
              className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center">
              <s.icon className="w-5 h-5 text-[#FBBC04] mx-auto mb-2" />
              <div className="text-3xl font-black text-white">{s.value}</div>
              <div className="text-xs text-gray-400 mt-1">{s.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Pain Point */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="bg-[#FBBC04]/10 border border-[#FBBC04]/30 rounded-2xl p-6 mb-12">
          <h2 className="text-xl font-bold text-[#FBBC04] mb-3">Il turista del 2026 e' cambiato</h2>
          <p className="text-gray-300 text-base leading-relaxed">
            Non legge piu le guide cartacee. Cerca <strong className="text-white">"cantine vicino a me"</strong> su Google Maps (ricerche cresciute del 900%).
            Guarda le foto. Se c'e' un Virtual Tour 360, ci entra. Vede le botti, la sala degustazione, i vigneti.
            <strong className="text-white"> Si innamora prima di partire. E quando arriva, ha gia deciso di comprare.</strong>
            <br /><br />
            Tu hai una cantina che merita. Ma se su Google sembra un garage, i turisti passano oltre.
          </p>
        </motion.div>

        {/* CTA */}
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4 }}
          className="bg-gradient-to-r from-[#34A853]/20 to-[#FBBC04]/20 border border-[#34A853]/30 rounded-2xl p-8 text-center mb-12">
          <h2 className="text-2xl font-black mb-2">Pacchetti da €99 — Deducibili al 100%</h2>
          <p className="text-gray-300 mb-6">PayPal 3 rate TAEG 0%. Timewarp Hyperlapse IN REGALO (video ipnotico dei tuoi spazi).</p>
          <a href={`https://wa.me/+393273839782?text=${encodeURIComponent(WA_MSG)}`}
            className="inline-flex items-center gap-2 bg-[#34A853] hover:bg-[#2d9249] text-white font-bold px-8 py-4 rounded-full text-lg transition-all hover:scale-105">
            <Phone className="w-5 h-5" /> Scrivimi su WhatsApp
          </a>
          <p className="text-gray-500 text-xs mt-3">Riccardo Rossi — Fotografo Google Certificato dal 2016</p>
        </motion.div>

        {/* Big Players */}
        <div className="mb-12">
          <h2 className="text-2xl font-black mb-2">Le Cantine Top della Toscana</h2>
          <p className="text-gray-400 text-base mb-6">Queste cantine attirano migliaia di visitatori ma <strong className="text-[#FBBC04]">quasi nessuna ha un Virtual Tour 360 su Google.</strong> Chi si muove per primo domina le ricerche.</p>
          <div className="grid gap-3">
            {BIG_PLAYERS.map((b, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 + i * 0.05 }}
                className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-xl px-5 py-3">
                <Wine className="w-5 h-5 text-[#FBBC04] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold text-base">{b.name}</div>
                  <div className="text-gray-400 text-xs">{b.note}</div>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500 flex-shrink-0">
                  <MapPin className="w-3 h-3" /> {b.zone}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Enoturismo stats */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-12">
          <h3 className="text-lg font-bold text-white mb-3">L'enoturismo toscano in numeri</h3>
          <ul className="space-y-2 text-gray-300 text-base">
            <li className="flex items-start gap-2"><Star className="w-4 h-4 text-[#FBBC04] mt-1 flex-shrink-0" /> Chianti, Montalcino, Montepulciano: <strong className="text-white">le 3 zone piu cercate</strong> su Google Maps per degustazioni</li>
            <li className="flex items-start gap-2"><Star className="w-4 h-4 text-[#FBBC04] mt-1 flex-shrink-0" /> Il turismo enogastronomico in Italia vale <strong className="text-white">€2.5 miliardi/anno</strong></li>
            <li className="flex items-start gap-2"><Star className="w-4 h-4 text-[#FBBC04] mt-1 flex-shrink-0" /> Il <strong className="text-white">28%</strong> di chi cerca da mobile visita entro 24 ore</li>
            <li className="flex items-start gap-2"><Star className="w-4 h-4 text-[#FBBC04] mt-1 flex-shrink-0" /> Un Virtual Tour costa <strong className="text-white">€99</strong> — il prezzo di 2 casse di Brunello</li>
          </ul>
        </div>

        {/* Final CTA */}
        <div className="text-center">
          <a href={`https://wa.me/+393273839782?text=${encodeURIComponent(WA_MSG)}`}
            className="inline-flex items-center gap-2 bg-[#EA4335] hover:bg-[#d33426] text-white font-bold px-8 py-4 rounded-full text-lg transition-all hover:scale-105">
            Voglio il Virtual Tour <ArrowRight className="w-5 h-5" />
          </a>
          <p className="text-gray-600 text-xs mt-4">schedabusiness.it — Fotografo Google Street View Certificato dal 2016</p>
        </div>
      </div>
    </div>
  );
}
